import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, ChefHat, TrendingUp, Edit, Eye, X, ArrowUpDown, Save, RotateCcw, AlertTriangle, Check } from 'lucide-react';
import { recipesAPI, pricingAPI } from '../../utils/api';
import { formatNumberForInput, parseFormattedNumber } from '../../utils/calculations';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  totalCost,
  priceFromMargin,
  marginFromPrice,
  percentOfPrice,
  marketPriceComparison,
  validateMargin,
  validatePrice,
  isNegativeMargin,
  formatMargin,
  formatPriceComparison
} from '../../utils/pricing';

// Inline Editing Row Component for Pricing
const PricingRow = ({ recipe, pricing, defaults, onSave, onCancel, isLoading }) => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  
  // Safety check for defaults
  if (!defaults) {
    console.error('Defaults not available for recipe:', recipe.id);
    return (
      <tr className="bg-destructive/10">
        <td colSpan="9" className="px-6 py-4 text-center text-destructive">
          Error: Costing defaults not available for this recipe. Please refresh the page.
        </td>
      </tr>
    );
  }
  
  // State for pricing data
  const [pricingData, setPricingData] = useState({
    fixedCostOverride: pricing?.fixed_cost_override || null,
    variableCostOverride: pricing?.variable_cost_override || null,
    profitMargin: pricing?.profit_margin !== null && pricing?.profit_margin !== undefined
      ? parseFloat(pricing.profit_margin)
      : null,
    profitCost: null, // Will be calculated later
    sellingPrice: pricing?.selling_price || null,
    sellingPricePercentage: null, // Will be calculated later
    markupPercentage: null, // Will be calculated later
    marketPrice: pricing?.market_price || null
  });

  // Calculate initial percentages for pre-filling
  const initialSellingPrice = pricing?.selling_price || 0;
  const initialFixedCost = pricing && pricing.fixed_cost_override !== null 
    ? pricing.fixed_cost_override 
    : defaults?.fixedCost || 0;
  const initialVariableCost = pricing && pricing.variable_cost_override !== null 
    ? pricing.variable_cost_override 
    : defaults?.variableCost || 0;
  
  const initialFixedCostPercentage = initialSellingPrice > 0 
    ? ((initialFixedCost / initialSellingPrice) * 100) === 0 ? '0.00' : ((initialFixedCost / initialSellingPrice) * 100).toFixed(2)
    : '0.00';
  const initialVariableCostPercentage = initialSellingPrice > 0 
    ? ((initialVariableCost / initialSellingPrice) * 100) === 0 ? '0.00' : ((initialVariableCost / initialSellingPrice) * 100).toFixed(2)
    : '0.00';

  // State for percentage overrides (frontend only)
  const [percentageOverrides, setPercentageOverrides] = useState({
    fixedCostPercentage: initialSellingPrice > 0 ? parseFloat(initialFixedCostPercentage) : null,
    variableCostPercentage: initialSellingPrice > 0 ? parseFloat(initialVariableCostPercentage) : null
  });

  // State for form inputs (display values)
  const [formInputs, setFormInputs] = useState({
    fixedCost: pricing && pricing.fixed_cost_override !== null 
      ? formatNumberForInput(pricing.fixed_cost_override) 
      : formatNumberForInput(defaults?.fixedCost || 0),
    variableCost: pricing && pricing.variable_cost_override !== null 
      ? formatNumberForInput(pricing.variable_cost_override) 
      : formatNumberForInput(defaults?.variableCost || 0),
    profitMargin: pricing && pricing.profit_margin !== null 
      ? formatNumberForInput(parseFloat(pricing.profit_margin)) 
      : '',
    profitCost: '', // Will be calculated later
    sellingPrice: pricing && pricing.selling_price !== null 
      ? formatNumberForInput(pricing.selling_price) 
      : '',
    sellingPricePercentage: '', // Will be calculated later
    markupPercentage: '', // Will be calculated later
    marketPrice: pricing && pricing.market_price !== null 
      ? formatNumberForInput(pricing.market_price) 
      : '',
    fixedCostPercentage: initialFixedCostPercentage, // Pre-fill with current percentage
    variableCostPercentage: initialVariableCostPercentage // Pre-fill with current percentage
  });
  
  // State for validation errors
  const [errors, setErrors] = useState({});
  
  // Ref to prevent infinite loops during auto-calculation
  const isCalculatingRef = useRef(false);
  
  // Ref to track if initial calculation has been done
  const initialCalculationDoneRef = useRef(false);
  
  // Ref to track if user is manually editing profit cost
  const isManuallyEditingProfitCostRef = useRef(false);
  
  // Ref to track which field user is currently editing
  const currentlyEditingFieldRef = useRef(null);
  
  // Ref to track if pricing calculation has been triggered (for enabling percentage fields)
  const hasCalculationTriggeredRef = useRef(!!pricing); // Enable immediately if editing existing pricing
  
  // Ref to track if auto-calculation is running (to prevent onBlur from interfering)
  const isAutoCalculatingRef = useRef(false);
  
  // Helper function to format percentage: show "0.00" for zero, otherwise 2 decimal places
  const formatPercentageForInput = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return rounded === 0 ? '0.00' : rounded.toFixed(2);
  };
  
  // Calculate selling price and profit margin first
  const currentSellingPrice = pricingData.sellingPrice ? Math.round(pricingData.sellingPrice * 100) / 100 : 0;
  const currentProfitMargin = pricingData.profitMargin || 0;
  const isNegativeProfitMargin = pricingData.profitMargin !== null && pricingData.profitMargin < 0;
  const isNegativeProfitCost = pricingData.profitCost !== null && pricingData.profitCost < 0;

  // Calculate current costs
  let currentFixedCost = pricingData.fixedCostOverride !== null 
    ? pricingData.fixedCostOverride 
    : defaults?.fixedCost || 0;
    
  let currentVariableCost = pricingData.variableCostOverride !== null 
    ? pricingData.variableCostOverride 
    : defaults?.variableCost || 0;

  // Note: Percentage overrides are only used for display, not for cost calculations
  // The actual costs remain as entered by the user
    
  const currentTotalCost = totalCost(
    recipe.cost_per_serving || 0,  // Use cost per serving instead of total cost
    currentFixedCost,
    currentVariableCost
  );
  
  // Calculate percentages of selling price
  const rawMaterialPercent = percentOfPrice(recipe.cost_per_serving || 0, currentSellingPrice);
  
  // Use percentage overrides if available, otherwise calculate from costs
  const fixedCostPercent = percentageOverrides.fixedCostPercentage !== null 
    ? percentageOverrides.fixedCostPercentage / 100 
    : percentOfPrice(currentFixedCost, currentSellingPrice);
  const variableCostPercent = percentageOverrides.variableCostPercentage !== null 
    ? percentageOverrides.variableCostPercentage / 100 
    : percentOfPrice(currentVariableCost, currentSellingPrice);
  
  // Market price comparison
  const marketComparison = marketPriceComparison(currentSellingPrice, pricingData.marketPrice);
  
  // Check for negative margin (only when selling price < total cost)
  const hasNegativeMargin = currentSellingPrice > 0 && currentSellingPrice < currentTotalCost;
  
  // Handle input changes with real-time auto-calculation
  const handleInputChange = useCallback((field, value) => {
    const numericValue = parseFormattedNumber(value);
    
    console.log(`🔍 handleInputChange - Field: ${field}, Value: "${value}", Numeric: ${numericValue}`);
    console.log(`🔍 isAutoCalculatingRef.current: ${isAutoCalculatingRef.current}`);
    
    // Track which field user is currently editing
    currentlyEditingFieldRef.current = field;
    console.log(`🔍 Currently editing field: ${field}`);
    
    // Reset calculation flag
    isCalculatingRef.current = false;
    
    // Track manual editing of profit cost
    if (field === 'profitCost') {
      isManuallyEditingProfitCostRef.current = true;
      console.log(`🔍 Profit Cost - Set isManuallyEditingProfitCostRef to true`);
    }
    
    // For fields that don't have real-time calculations, update formInputs immediately
    // For fields with real-time calculations, let the calculation logic handle the updates
    const fieldsWithRealTimeCalc = ['fixedCost', 'variableCost', 'profitMargin', 'sellingPrice', 'profitCost', 'sellingPricePercentage'];
    
    if (!fieldsWithRealTimeCalc.includes(field)) {
      setFormInputs(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Map field names to the correct pricing data field names
    let pricingField = field;
    if (field === 'fixedCost') {
      pricingField = 'fixedCostOverride';
    } else if (field === 'variableCost') {
      pricingField = 'variableCostOverride';
    }
    
    // Update pricing data - treat empty values as null, otherwise use numeric value
    setPricingData(prev => {
      const newData = {
        ...prev,
        [pricingField]: value.trim() === '' ? null : numericValue
      };
      
      // Handle clearing of Fixed % and Variable % when corresponding costs are cleared or set to 0
      if ((field === 'fixedCost' || field === 'variableCost') && (value.trim() === '' || numericValue === 0)) {
        // Update formInputs for the current field
        setFormInputs(formPrev => ({
          ...formPrev,
          [field]: value
        }));
        
        // Clear the corresponding percentage field
        if (field === 'fixedCost') {
          setFormInputs(formPrev => ({
            ...formPrev,
            fixedCostPercentage: '0.00'
          }));
          setPercentageOverrides(prevOverrides => ({
            ...prevOverrides,
            fixedCostPercentage: null
          }));
          // Also update the pricing data to ensure consistency
          setPricingData(prev => ({
            ...prev,
            fixedCostOverride: 0
          }));
        } else if (field === 'variableCost') {
          setFormInputs(formPrev => ({
            ...formPrev,
            variableCostPercentage: '0.00'
          }));
          setPercentageOverrides(prevOverrides => ({
            ...prevOverrides,
            variableCostPercentage: null
          }));
          // Also update the pricing data to ensure consistency
          setPricingData(prev => ({
            ...prev,
            variableCostOverride: 0
          }));
        }
        
        // Don't proceed with real-time calculation when clearing
        return newData;
      }
      
      // Handle real-time calculation when Fixed Cost or Variable Cost changes (but not when clearing)
      if ((field === 'fixedCost' || field === 'variableCost') && numericValue > 0) {
        // Calculate new total cost
        const newTotalCost = totalCost(
          recipe.cost_per_serving || 0,  // Use cost per serving instead of total cost
          field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0),
          field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0)
        );
        
        const fixedCostValue = field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0);
        const variableCostValue = field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0);

        // If selling price is set, keep it fixed and recalculate profit margin (Excel / menu price model)
        const existingSellingPrice = parseFloat(prev.sellingPrice) || 0;
        if (existingSellingPrice > 0 && newTotalCost > 0) {
          const roundedSellingPrice = Math.round(existingSellingPrice * 100) / 100;
          const newMargin = marginFromPrice(newTotalCost, roundedSellingPrice);
          const newProfitCost = roundedSellingPrice - newTotalCost;
          const newPercentage = Math.round(((roundedSellingPrice / newTotalCost) * 100) * 1000) / 1000;

          setFormInputs(formPrev => ({
            ...formPrev,
            [field]: value,
            sellingPrice: roundedSellingPrice.toFixed(2),
            profitMargin: (newMargin * 100).toFixed(2),
            profitCost: newProfitCost.toFixed(2),
            sellingPricePercentage: newPercentage.toFixed(2),
            fixedCostPercentage: formatPercentageForInput((fixedCostValue / roundedSellingPrice) * 100),
            variableCostPercentage: formatPercentageForInput((variableCostValue / roundedSellingPrice) * 100),
          }));

          setPercentageOverrides(prevOverrides => ({
            ...prevOverrides,
            fixedCostPercentage: (fixedCostValue / roundedSellingPrice) * 100,
            variableCostPercentage: (variableCostValue / roundedSellingPrice) * 100,
          }));

          return {
            ...newData,
            sellingPrice: roundedSellingPrice,
            profitMargin: newMargin * 100,
            profitCost: newProfitCost,
            sellingPricePercentage: newPercentage,
          };
        }
        // If pricing by margin % only (no selling price yet), recalculate selling price
        if (prev.profitMargin !== null && prev.profitMargin !== 0 && newTotalCost > 0) {
          const newSellingPrice = priceFromMargin(newTotalCost, prev.profitMargin / 100);
          const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100; // Round to 2 decimal places
          const newProfitCost = roundedSellingPrice - newTotalCost;
          const newPercentage = Math.round(((roundedSellingPrice / newTotalCost) * 100) * 1000) / 1000;
          
          // Update form inputs with new calculated values
            setFormInputs(formPrev => ({
              ...formPrev,
              [field]: value, // Update the current field with user input
              sellingPrice: roundedSellingPrice.toFixed(2),
              profitMargin: prev.profitMargin.toFixed(2), // Keep margin unchanged
              profitCost: newProfitCost.toFixed(2), // Recalculate profit cost
              sellingPricePercentage: newPercentage.toFixed(2),
            // Update percentage fields with calculated values using rounded selling price
            fixedCostPercentage: formatPercentageForInput(((field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0)) / roundedSellingPrice) * 100),
            variableCostPercentage: formatPercentageForInput(((field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0)) / roundedSellingPrice) * 100)
          }));
          
          // Update percentage overrides
          setPercentageOverrides(prevOverrides => ({
            ...prevOverrides,
            fixedCostPercentage: ((field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0)) / roundedSellingPrice) * 100,
            variableCostPercentage: ((field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0)) / roundedSellingPrice) * 100
          }));
          
          return {
            ...newData,
            sellingPrice: roundedSellingPrice,
            profitMargin: prev.profitMargin, // Keep margin unchanged when no selling price set yet
            profitCost: newProfitCost, // Recalculate profit cost
            sellingPricePercentage: newPercentage
          };
        }
        // If we have a profit cost, recalculate selling price and margin while keeping profit cost unchanged
        else if (prev.profitCost !== null && prev.profitCost > 0 && newTotalCost > 0) {
          const newSellingPrice = newTotalCost + prev.profitCost;
          const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100; // Round to 2 decimal places
          const newMargin = marginFromPrice(newTotalCost, roundedSellingPrice);
          const newPercentage = Math.round(((roundedSellingPrice / newTotalCost) * 100) * 1000) / 1000;
          
          // Update form inputs with new calculated values
            setFormInputs(formPrev => ({
              ...formPrev,
              [field]: value, // Update the current field with user input
              sellingPrice: roundedSellingPrice.toFixed(2),
              profitMargin: (newMargin * 100).toFixed(2),
              sellingPricePercentage: newPercentage.toFixed(2),
            // Update percentage fields with calculated values using rounded selling price
            fixedCostPercentage: formatPercentageForInput(((field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0)) / roundedSellingPrice) * 100),
            variableCostPercentage: formatPercentageForInput(((field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0)) / roundedSellingPrice) * 100)
          }));
          
          // Update percentage overrides
          setPercentageOverrides(prevOverrides => ({
            ...prevOverrides,
            fixedCostPercentage: ((field === 'fixedCost' ? numericValue : (prev.fixedCostOverride !== null ? prev.fixedCostOverride : defaults?.fixedCost || 0)) / roundedSellingPrice) * 100,
            variableCostPercentage: ((field === 'variableCost' ? numericValue : (prev.variableCostOverride !== null ? prev.variableCostOverride : defaults?.variableCost || 0)) / roundedSellingPrice) * 100
          }));
          
          return {
            ...newData,
            sellingPrice: roundedSellingPrice,
            profitMargin: newMargin * 100,
            sellingPricePercentage: newPercentage
          };
        }
        
        // If no profit margin or profit cost exists, just update the current field
        setFormInputs(formPrev => ({
          ...formPrev,
          [field]: value
        }));
      }
      
      return newData;
    });
    
    // Clear related fields when one is entered (but not when clearing)
    if (field === 'profitMargin' && numericValue !== 0 && value.trim() !== '') {
      setFormInputs(prev => ({ ...prev, [field]: value, sellingPrice: '', profitCost: '', sellingPricePercentage: '', markupPercentage: '' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitCost: null, sellingPricePercentage: null, markupPercentage: null }));
    } else if (field === 'sellingPrice' && numericValue > 0) {
      setFormInputs(prev => ({ ...prev, [field]: value, profitMargin: '', profitCost: '', sellingPricePercentage: '', markupPercentage: '' }));
      setPricingData(prev => ({ ...prev, profitMargin: null, profitCost: null, sellingPricePercentage: null, markupPercentage: null }));
    } else if (field === 'profitCost' && numericValue > 0) {
      setFormInputs(prev => ({ ...prev, [field]: value, sellingPrice: '', profitMargin: '', sellingPricePercentage: '', markupPercentage: '' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitMargin: null, sellingPricePercentage: null, markupPercentage: null }));
    } else if (field === 'sellingPricePercentage' && numericValue > 0) {
      setFormInputs(prev => ({ ...prev, [field]: value, sellingPrice: '', profitMargin: '', profitCost: '', markupPercentage: '' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitMargin: null, profitCost: null, markupPercentage: null }));
      } else if (fieldsWithRealTimeCalc.includes(field)) {
        // For other real-time calculation fields, just update the current field
        setFormInputs(prev => ({ ...prev, [field]: value }));
      }
      
      // Clear percentage fields when selling price is cleared
      if (field === 'sellingPrice' && value.trim() === '') {
        setFormInputs(prev => ({ ...prev, fixedCostPercentage: '0.00', variableCostPercentage: '0.00' }));
        setPercentageOverrides(prev => ({ ...prev, fixedCostPercentage: null, variableCostPercentage: null }));
      }
      
      // If user clears a field, also clear the related fields
    if (field === 'profitMargin' && value.trim() === '') {
      setFormInputs(prev => ({ ...prev, sellingPrice: '', profitCost: '', sellingPricePercentage: '', markupPercentage: '', fixedCostPercentage: '0.00', variableCostPercentage: '0.00' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitCost: null, sellingPricePercentage: null, markupPercentage: null }));
      setPercentageOverrides(prev => ({ ...prev, fixedCostPercentage: null, variableCostPercentage: null }));
    } else if (field === 'sellingPrice' && value.trim() === '') {
      setFormInputs(prev => ({ ...prev, profitMargin: '', profitCost: '', sellingPricePercentage: '', markupPercentage: '', fixedCostPercentage: '0.00', variableCostPercentage: '0.00' }));
      setPricingData(prev => ({ ...prev, profitMargin: null, profitCost: null, sellingPricePercentage: null, markupPercentage: null }));
      setPercentageOverrides(prev => ({ ...prev, fixedCostPercentage: null, variableCostPercentage: null }));
    } else if (field === 'profitCost' && value.trim() === '') {
      setFormInputs(prev => ({ ...prev, sellingPrice: '', profitMargin: '', sellingPricePercentage: '', markupPercentage: '', fixedCostPercentage: '0.00', variableCostPercentage: '0.00' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitMargin: null, sellingPricePercentage: null, markupPercentage: null }));
      setPercentageOverrides(prev => ({ ...prev, fixedCostPercentage: null, variableCostPercentage: null }));
    } else if (field === 'sellingPricePercentage' && value.trim() === '') {
      setFormInputs(prev => ({ ...prev, sellingPrice: '', profitMargin: '', profitCost: '', markupPercentage: '', fixedCostPercentage: '0.00', variableCostPercentage: '0.00' }));
      setPricingData(prev => ({ ...prev, sellingPrice: null, profitMargin: null, profitCost: null, markupPercentage: null }));
      setPercentageOverrides(prev => ({ ...prev, fixedCostPercentage: null, variableCostPercentage: null }));
    }
    
    // Handle percentage override inputs
    if (field === 'fixedCostPercentage' || field === 'variableCostPercentage') {
      const percentageValue = parseFormattedNumber(value);
      setPercentageOverrides(prev => ({
        ...prev,
        [field === 'fixedCostPercentage' ? 'fixedCostPercentage' : 'variableCostPercentage']:
          value.trim() === '' ? null : percentageValue
      }));
      
      // Handle clearing of Fixed Cost or Variable Cost when corresponding percentage is cleared or set to 0
      if (value.trim() === '' || percentageValue === 0) {
        if (field === 'fixedCostPercentage') {
          setFormInputs(formPrev => ({
            ...formPrev,
            fixedCost: '0.00'
          }));
          setPricingData(prev => ({
            ...prev,
            fixedCostOverride: 0
          }));
        } else if (field === 'variableCostPercentage') {
          setFormInputs(formPrev => ({
            ...formPrev,
            variableCost: '0.00'
          }));
          setPricingData(prev => ({
            ...prev,
            variableCostOverride: 0
          }));
        }
      } else if (percentageValue > 0) {
        // Calculate cost amount from percentage when percentage is entered
        // Use currentSellingPrice if available, otherwise use currentTotalCost as fallback
        const sellingPriceForCalculation = currentSellingPrice > 0 ? currentSellingPrice : currentTotalCost;
        const calculatedCost = (sellingPriceForCalculation * percentageValue) / 100;
        
        if (field === 'fixedCostPercentage') {
          setFormInputs(formPrev => ({
            ...formPrev,
            fixedCost: calculatedCost.toFixed(2)
          }));
          setPricingData(prev => ({
            ...prev,
            fixedCostOverride: calculatedCost
          }));
        } else if (field === 'variableCostPercentage') {
          setFormInputs(formPrev => ({
            ...formPrev,
            variableCost: calculatedCost.toFixed(2)
          }));
          setPricingData(prev => ({
            ...prev,
            variableCostOverride: calculatedCost
          }));
        }
      }
    }
    
    // Track when pricing calculations are triggered (for enabling percentage fields)
    if (field === 'profitMargin' || field === 'sellingPrice' || field === 'profitCost' || field === 'sellingPricePercentage') {
      hasCalculationTriggeredRef.current = true;
    }
    
    // Clear errors
    setErrors(prev => ({
      ...prev,
      [field]: null
    }));
  }, [currentTotalCost]);
  
  // Handle reset for fixed/variable costs
  const handleReset = (field) => {
    if (field === 'fixedCost') {
      setFormInputs(prev => ({
        ...prev,
        fixedCost: formatNumberForInput(defaults?.fixedCost || 0)
      }));
      setPricingData(prev => ({
        ...prev,
        fixedCostOverride: null
      }));
    } else if (field === 'variableCost') {
      setFormInputs(prev => ({
        ...prev,
        variableCost: formatNumberForInput(defaults?.variableCost || 0)
      }));
      setPricingData(prev => ({
        ...prev,
        variableCostOverride: null
      }));
    }
  };

  
  // Real-time auto-calculation effect
  useEffect(() => {
    // Prevent infinite loops
    if (isCalculatingRef.current) return;
    
    // Only auto-calculate if we have a valid total cost
    if (currentTotalCost <= 0) return;
    
    try {
       // If user enters Margin %
       if (pricingData.profitMargin !== null && pricingData.profitMargin !== 0 &&
           (pricingData.sellingPrice === null || pricingData.sellingPrice === 0) &&
           (pricingData.profitCost === null || pricingData.profitCost === 0) &&
           (pricingData.sellingPricePercentage === null || pricingData.sellingPricePercentage === 0)) {
        
        console.log(`🔍 Auto-calculation - Margin % calculation triggered`);
        const marginDecimal = pricingData.profitMargin / 100;
        
        // Handle edge cases for margin >= 100%
        let newSellingPrice, newProfitCost, newMarkupPercentage, newSellingPricePercentage;
        
        if (pricingData.profitMargin >= 100) {
          // For margin >= 100%, use alternative calculation
          // Selling Price = Total Cost + (Total Cost * Margin % / 100)
          newSellingPrice = currentTotalCost + (currentTotalCost * marginDecimal);
          newProfitCost = currentTotalCost * marginDecimal;
          newMarkupPercentage = marginDecimal * 100; // Markup % = Margin %
          newSellingPricePercentage = 100 + newMarkupPercentage;
        } else {
          // Standard calculation for margin < 100%
          newSellingPrice = currentTotalCost / (1 - marginDecimal);
          newProfitCost = (currentTotalCost * marginDecimal) / (1 - marginDecimal);
          newMarkupPercentage = ((newSellingPrice - currentTotalCost) / currentTotalCost) * 100;
          newSellingPricePercentage = 100 + newMarkupPercentage;
        }
        
        // Round selling price to 2 decimal places for consistency
        const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100;
        
        isCalculatingRef.current = true;
        isAutoCalculatingRef.current = true;
        console.log(`🔍 Auto-calculation - isAutoCalculatingRef set to true`);
        setFormInputs(prev => ({
          ...prev,
          profitMargin: currentlyEditingFieldRef.current === 'profitMargin' ? prev.profitMargin : newMarginPercentage.toFixed(2),
          sellingPrice: currentlyEditingFieldRef.current === 'sellingPrice' ? prev.sellingPrice : roundedSellingPrice.toFixed(2),
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : newProfitCost.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : newSellingPricePercentage.toFixed(2),
          markupPercentage: newMarkupPercentage.toFixed(2),
          fixedCostPercentage: formatPercentageForInput((currentFixedCost / roundedSellingPrice) * 100),
          variableCostPercentage: formatPercentageForInput((currentVariableCost / roundedSellingPrice) * 100)
        }));
        setPricingData(prev => ({
          ...prev,
          sellingPrice: roundedSellingPrice,
          profitCost: newProfitCost,
          sellingPricePercentage: newSellingPricePercentage,
          markupPercentage: newMarkupPercentage
        }));
        setPercentageOverrides(prev => ({
          ...prev,
          fixedCostPercentage: (currentFixedCost / roundedSellingPrice) * 100,
          variableCostPercentage: (currentVariableCost / roundedSellingPrice) * 100
        }));
        isCalculatingRef.current = false;
        isAutoCalculatingRef.current = false;
      }
      // Handle explicit 0% profit margin (break-even)
      else if (pricingData.profitMargin === 0 && 
               (pricingData.sellingPrice === null || pricingData.sellingPrice === 0) &&
               (pricingData.profitCost === null || pricingData.profitCost === 0) &&
               (pricingData.sellingPricePercentage === null || pricingData.sellingPricePercentage === 0)) {
        isCalculatingRef.current = true;
        isAutoCalculatingRef.current = true;
        setFormInputs(prev => ({
          ...prev,
          sellingPrice: currentlyEditingFieldRef.current === 'sellingPrice' ? prev.sellingPrice : currentTotalCost.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : "100.00",
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : "0.00",
          markupPercentage: "0.00",
          fixedCostPercentage: formatPercentageForInput((currentFixedCost / currentTotalCost) * 100),
          variableCostPercentage: formatPercentageForInput((currentVariableCost / currentTotalCost) * 100)
        }));
        setPricingData(prev => ({
          ...prev,
          sellingPrice: currentTotalCost,
          sellingPricePercentage: 100,
          profitCost: 0,
          markupPercentage: 0
        }));
        setPercentageOverrides(prev => ({
          ...prev,
          fixedCostPercentage: (currentFixedCost / currentTotalCost) * 100,
          variableCostPercentage: (currentVariableCost / currentTotalCost) * 100
        }));
        isCalculatingRef.current = false;
        isAutoCalculatingRef.current = false;
      } 
      // If user enters Profit Cost (including 0)
      else if (pricingData.profitCost !== null && pricingData.profitCost >= 0 && 
               (pricingData.sellingPrice === null || pricingData.sellingPrice === 0) &&
               (pricingData.profitMargin === null || pricingData.profitMargin === 0) &&
               (pricingData.sellingPricePercentage === null || pricingData.sellingPricePercentage === 0)) {
        // Handle Profit Cost = 0 case
        let newSellingPrice, newMarginPercentage, newMarkupPercentage, newSellingPricePercentage;
        
        if (pricingData.profitCost === 0) {
          // For 0 profit cost, selling price equals total cost
          newSellingPrice = currentTotalCost;
          newMarginPercentage = 0;
          newMarkupPercentage = 0;
          newSellingPricePercentage = 100;
        } else {
          // For positive profit cost
          newSellingPrice = currentTotalCost + pricingData.profitCost;
          newMarginPercentage = (pricingData.profitCost / newSellingPrice) * 100;
          newMarkupPercentage = (pricingData.profitCost / currentTotalCost) * 100;
          newSellingPricePercentage = 100 + newMarkupPercentage;
        }
        
        // Round selling price to 2 decimal places for consistency
        const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100;
        
        isCalculatingRef.current = true;
        isAutoCalculatingRef.current = true;
        setFormInputs(prev => ({
          ...prev,
          sellingPrice: currentlyEditingFieldRef.current === 'sellingPrice' ? prev.sellingPrice : roundedSellingPrice.toFixed(2),
          profitMargin: currentlyEditingFieldRef.current === 'profitMargin' ? prev.profitMargin : newMarginPercentage.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : newSellingPricePercentage.toFixed(2),
          markupPercentage: newMarkupPercentage.toFixed(2),
          fixedCostPercentage: formatPercentageForInput((currentFixedCost / roundedSellingPrice) * 100),
          variableCostPercentage: formatPercentageForInput((currentVariableCost / roundedSellingPrice) * 100)
        }));
        setPricingData(prev => ({
          ...prev,
          sellingPrice: roundedSellingPrice,
          profitMargin: newMarginPercentage,
          sellingPricePercentage: newSellingPricePercentage,
          markupPercentage: newMarkupPercentage
        }));
        setPercentageOverrides(prev => ({
          ...prev,
          fixedCostPercentage: (currentFixedCost / roundedSellingPrice) * 100,
          variableCostPercentage: (currentVariableCost / roundedSellingPrice) * 100
        }));
        isCalculatingRef.current = false;
        isAutoCalculatingRef.current = false;
      }
      // If user enters % of Cost (Selling Price Percentage)
      else if (pricingData.sellingPricePercentage !== null && pricingData.sellingPricePercentage > 0 && 
               (pricingData.sellingPrice === null || pricingData.sellingPrice === 0) &&
               (pricingData.profitMargin === null || pricingData.profitMargin === 0) &&
               (pricingData.profitCost === null || pricingData.profitCost === 0)) {
        const markupPercentage = pricingData.sellingPricePercentage - 100;
        const newSellingPrice = currentTotalCost * (1 + (markupPercentage / 100));
        const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100; // Round to 2 decimal places
        const newProfitCost = currentTotalCost * (markupPercentage / 100);
        const newMarginPercentage = ((markupPercentage) / (100 + markupPercentage)) * 100;
        
        isCalculatingRef.current = true;
        isAutoCalculatingRef.current = true;
        setFormInputs(prev => ({
          ...prev,
          sellingPrice: currentlyEditingFieldRef.current === 'sellingPrice' ? prev.sellingPrice : roundedSellingPrice.toFixed(2),
          profitMargin: currentlyEditingFieldRef.current === 'profitMargin' ? prev.profitMargin : newMarginPercentage.toFixed(2),
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : newProfitCost.toFixed(2),
          markupPercentage: markupPercentage.toFixed(2),
          fixedCostPercentage: formatPercentageForInput((currentFixedCost / roundedSellingPrice) * 100),
          variableCostPercentage: formatPercentageForInput((currentVariableCost / roundedSellingPrice) * 100)
        }));
        setPricingData(prev => ({
          ...prev,
          sellingPrice: roundedSellingPrice,
          profitMargin: newMarginPercentage,
          profitCost: newProfitCost,
          markupPercentage: markupPercentage
        }));
        setPercentageOverrides(prev => ({
          ...prev,
          fixedCostPercentage: (currentFixedCost / roundedSellingPrice) * 100,
          variableCostPercentage: (currentVariableCost / roundedSellingPrice) * 100
        }));
        isCalculatingRef.current = false;
        isAutoCalculatingRef.current = false;
      }
      // If user enters Selling Price
      else if (pricingData.sellingPrice !== null && pricingData.sellingPrice > 0 && 
               (pricingData.profitMargin === null || pricingData.profitMargin === 0) &&
               (pricingData.profitCost === null || pricingData.profitCost === 0) &&
               (pricingData.sellingPricePercentage === null || pricingData.sellingPricePercentage === 0)) {
        const newMarginPercentage = ((pricingData.sellingPrice - currentTotalCost) / pricingData.sellingPrice) * 100;
        
        // Handle edge cases for margin >= 100%
        let newProfitCost;
        if (newMarginPercentage >= 100) {
          // For margin >= 100%, use alternative calculation
          newProfitCost = pricingData.sellingPrice - currentTotalCost;
        } else {
          // Standard calculation for margin < 100%
          newProfitCost = (currentTotalCost * newMarginPercentage) / (100 - newMarginPercentage);
        }
        
        const newMarkupPercentage = ((pricingData.sellingPrice - currentTotalCost) / currentTotalCost) * 100;
        const newSellingPricePercentage = 100 + newMarkupPercentage;
        
        // Round selling price to 2 decimal places for consistency
        const roundedSellingPrice = Math.round(pricingData.sellingPrice * 100) / 100;
        
        isCalculatingRef.current = true;
        isAutoCalculatingRef.current = true;
        setFormInputs(prev => ({
          ...prev,
          profitMargin: currentlyEditingFieldRef.current === 'profitMargin' ? prev.profitMargin : newMarginPercentage.toFixed(2),
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : newProfitCost.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : newSellingPricePercentage.toFixed(2),
          markupPercentage: newMarkupPercentage.toFixed(2),
          fixedCostPercentage: formatPercentageForInput((currentFixedCost / roundedSellingPrice) * 100),
          variableCostPercentage: formatPercentageForInput((currentVariableCost / roundedSellingPrice) * 100)
        }));
        setPricingData(prev => ({
          ...prev,
          profitMargin: newMarginPercentage,
          profitCost: newProfitCost,
          sellingPricePercentage: newSellingPricePercentage,
          markupPercentage: newMarkupPercentage
        }));
        setPercentageOverrides(prev => ({
          ...prev,
          fixedCostPercentage: (currentFixedCost / roundedSellingPrice) * 100,
          variableCostPercentage: (currentVariableCost / roundedSellingPrice) * 100
        }));
        isCalculatingRef.current = false;
        isAutoCalculatingRef.current = false;
      }
    } catch (error) {
      console.error('Auto-calculation error:', error);
      isCalculatingRef.current = false;
    }
  }, [currentTotalCost, pricingData.profitMargin, pricingData.sellingPrice, pricingData.profitCost, pricingData.sellingPricePercentage]);
  
  // Real-time calculation when Fixed Cost, Variable Cost, Fixed %, or Variable % changes (keeping Margin % unchanged)
  useEffect(() => {
    console.log(`🔍 Real-time calculation useEffect triggered - Dependencies: currentTotalCost=${currentTotalCost}, currentFixedCost=${currentFixedCost}, currentVariableCost=${currentVariableCost}, profitMargin=${pricingData.profitMargin}, fixedCostPercentage=${percentageOverrides.fixedCostPercentage}, variableCostPercentage=${percentageOverrides.variableCostPercentage}`);
    console.log(`🔍 Real-time calculation check - Margin: ${pricingData.profitMargin}, Total Cost: ${currentTotalCost}, Fixed Cost: ${currentFixedCost}, Variable Cost: ${currentVariableCost}, Currently editing: ${currentlyEditingFieldRef.current}, Is calculating: ${isCalculatingRef.current}`);
    
    // Check each condition individually for debugging
    const hasMargin = pricingData.profitMargin !== null;
    const hasTotalCost = currentTotalCost > 0;
    const hasCosts = (currentFixedCost >= 0 && currentVariableCost >= 0); // Allow both costs to be 0
    const notCalculating = !isCalculatingRef.current;
    
    console.log(`🔍 Condition checks - hasMargin: ${hasMargin}, hasTotalCost: ${hasTotalCost}, hasCosts: ${hasCosts}, notCalculating: ${notCalculating}`);
    
    // Only calculate if we have a margin (including 0%) and total cost > 0, and costs are valid (>= 0)
    // Run for all fields including percentage fields
    if (hasMargin && hasTotalCost && hasCosts && notCalculating) {
      isCalculatingRef.current = true;
      isAutoCalculatingRef.current = true;
      
      console.log(`🔍 Real-time calculation triggered - Margin %: ${pricingData.profitMargin}%, Total Cost: ${currentTotalCost}, Fixed Cost: ${currentFixedCost}, Variable Cost: ${currentVariableCost}`);
      
      // Keep the margin unchanged and calculate new selling price and profit cost
      const marginDecimal = pricingData.profitMargin / 100;
      
      // Handle edge cases for margin >= 100% and margin = 0%
      let newSellingPrice, newProfitCost, newPercentage;
      
      if (pricingData.profitMargin === 0) {
        // For 0% margin, selling price equals total cost
        newSellingPrice = currentTotalCost;
        newProfitCost = 0;
        newPercentage = 100;
      } else if (pricingData.profitMargin >= 100) {
        // For margin >= 100%, use alternative calculation
        newSellingPrice = currentTotalCost + (currentTotalCost * marginDecimal);
        newProfitCost = currentTotalCost * marginDecimal;
        newPercentage = 100 + (marginDecimal * 100);
      } else {
        // Standard calculation for margin < 100%
        newSellingPrice = currentTotalCost / (1 - marginDecimal);
        newProfitCost = (currentTotalCost * marginDecimal) / (1 - marginDecimal);
      }
      
      // Round selling price to 2 decimal places for consistency
      const roundedSellingPrice = Math.round(newSellingPrice * 100) / 100;
      newPercentage = Math.round(((roundedSellingPrice / currentTotalCost) * 100) * 1000) / 1000;
      
      console.log(`🔍 Calculated values - Selling Price: ${roundedSellingPrice}, Profit Cost: ${newProfitCost}, Fixed %: ${(currentFixedCost / roundedSellingPrice) * 100}, Variable %: ${(currentVariableCost / roundedSellingPrice) * 100}`);
      
        setFormInputs(prev => ({
          ...prev,
          sellingPrice: currentlyEditingFieldRef.current === 'sellingPrice' ? prev.sellingPrice : roundedSellingPrice.toFixed(2),
          profitMargin: currentlyEditingFieldRef.current === 'profitMargin' ? prev.profitMargin : pricingData.profitMargin.toFixed(2), // Keep margin unchanged
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : newProfitCost.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : newPercentage.toFixed(2),
        // Update percentage fields with calculated values, but preserve user input if they're currently editing
        fixedCostPercentage: currentlyEditingFieldRef.current === 'fixedCostPercentage' ? prev.fixedCostPercentage : formatPercentageForInput((currentFixedCost / roundedSellingPrice) * 100),
        variableCostPercentage: currentlyEditingFieldRef.current === 'variableCostPercentage' ? prev.variableCostPercentage : formatPercentageForInput((currentVariableCost / roundedSellingPrice) * 100)
      }));
      
      setPricingData(prev => ({
        ...prev,
        sellingPrice: roundedSellingPrice,
        profitMargin: pricingData.profitMargin, // Keep margin unchanged
        profitCost: newProfitCost,
        sellingPricePercentage: newPercentage
      }));
      
      setPercentageOverrides(prev => ({
        ...prev,
        fixedCostPercentage: currentlyEditingFieldRef.current === 'fixedCostPercentage' ? prev.fixedCostPercentage : (currentFixedCost / roundedSellingPrice) * 100,
        variableCostPercentage: currentlyEditingFieldRef.current === 'variableCostPercentage' ? prev.variableCostPercentage : (currentVariableCost / roundedSellingPrice) * 100
      }));
      
      isCalculatingRef.current = false;
    }
  }, [currentTotalCost, currentFixedCost, currentVariableCost, pricingData.profitMargin, percentageOverrides.fixedCostPercentage, percentageOverrides.variableCostPercentage]);
  
  // Calculate initial profit cost and selling price percentage when there's existing pricing data
  useEffect(() => {
    if (pricing && pricing.selling_price && currentTotalCost > 0 && !initialCalculationDoneRef.current) {
      const sellingPrice = parseFloat(pricing.selling_price);
      const margin = marginFromPrice(currentTotalCost, sellingPrice);
      const profitCost = Math.round(((currentTotalCost * margin) / (1 - margin)) * 1000) / 1000;
      const sellingPricePercentage = Math.round(((sellingPrice / currentTotalCost) * 100) * 1000) / 1000;
      
        setFormInputs(prev => ({
          ...prev,
          profitCost: currentlyEditingFieldRef.current === 'profitCost' ? prev.profitCost : profitCost.toFixed(2),
          sellingPricePercentage: currentlyEditingFieldRef.current === 'sellingPricePercentage' ? prev.sellingPricePercentage : sellingPricePercentage.toFixed(2)
      }));
      setPricingData(prev => ({
        ...prev,
        profitCost: profitCost,
        sellingPricePercentage: sellingPricePercentage
      }));
      
      // Mark initial calculation as done
      initialCalculationDoneRef.current = true;
    }
  }, [pricing, currentTotalCost]);
  
  // Handle save
  const handleSave = () => {
    // Validate required fields
    const newErrors = {};
    
    if (!pricingData.profitMargin && !pricingData.sellingPrice && !pricingData.profitCost && !pricingData.sellingPricePercentage) {
      newErrors.general = t('pricing.messages.enterOneOfMarginOrPrice');
    }
    
    if (pricingData.profitMargin !== null) {
      // Validate profit margin as percentage (10 for 10%)
      const marginValidation = validateMargin(pricingData.profitMargin);
      if (!marginValidation.isValid) {
        newErrors.profitMargin = marginValidation.error;
      }
    }
    
    if (pricingData.sellingPrice !== null) {
      const priceValidation = validatePrice(pricingData.sellingPrice);
      if (!priceValidation.isValid) {
        newErrors.sellingPrice = priceValidation.error;
      }
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      // Convert profit margin from percentage to decimal for backend
      // Get current values from form inputs for fixed and variable costs
      const currentFixedCost = parseFormattedNumber(formInputs.fixedCost);
      const currentVariableCost = parseFormattedNumber(formInputs.variableCost);
      
      const dataToSave = {
        ...pricingData,
        fixedCostOverride: currentFixedCost,
        variableCostOverride: currentVariableCost,
        profitMargin: pricingData.profitMargin !== null ? Math.round(pricingData.profitMargin * 100) / 100 : null, // Round to 2 decimal places
        sellingPrice: pricingData.sellingPrice ? Math.round(pricingData.sellingPrice * 100) / 100 : null, // Round to 2 decimal places
        profitCost: pricingData.profitCost ? Math.round(pricingData.profitCost * 100) / 100 : null, // Round to 2 decimal places
        sellingPricePercentage: pricingData.sellingPricePercentage ? Math.round(pricingData.sellingPricePercentage * 100) / 100 : null, // Round to 2 decimal places
        markupPercentage: pricingData.markupPercentage ? Math.round(pricingData.markupPercentage * 100) / 100 : null, // Round to 2 decimal places
        marketPrice: pricingData.marketPrice ? Math.round(pricingData.marketPrice * 100) / 100 : null // Round to 2 decimal places
      };
      
      
      onSave(recipe.id, {
        recipeId: recipe.id,
        ...dataToSave,
        totalCost: currentTotalCost
      });
    }
  };
  
  // Check if save is disabled
  const isSaveDisabled = !pricingData.profitMargin && !pricingData.sellingPrice && !pricingData.profitCost && !pricingData.sellingPricePercentage;
  
  return (
    <tr className="bg-primary/10">
      <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground" style={{ fontSize: '0.7rem' }}>
        #{recipe.id}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-left">
        <div className="dish-item flex items-start justify-start">
          <div className="flex-shrink-0 h-10 w-10">
            {recipe.image_url ? (
              <img
                className="h-10 w-10 rounded-lg object-cover"
                src={recipe.image_url}
                alt={recipe.name_en}
              />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                <ChefHat className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="ml-4 text-left">
            {i18n.language === 'ar' ? (
              <>
                {recipe.name_ar && (
                  <div className="text-xs font-medium text-card-foreground font-arabic" style={{ fontSize: '0.7rem' }}>
                    {recipe.name_ar}
                  </div>
                )}
                <div className="text-xs text-muted-foreground" style={{ fontSize: '0.7rem' }}>
                  {recipe.name_en}
                </div>
              </>
            ) : (
              <>
                <div className="text-xs font-medium text-card-foreground" style={{ fontSize: '0.7rem' }}>
                  {recipe.name_en}
                </div>
                {recipe.name_ar && (
                  <div className="text-xs text-muted-foreground font-arabic" style={{ fontSize: '0.7rem' }}>
                    {recipe.name_ar}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="font-medium">
          {formatCurrency(recipe.cost_per_serving || 0)}
        </div>
        <div className="text-muted-foreground">
          {currentSellingPrice > 0 ? ((rawMaterialPercent * 100) === 0 ? '0.00' : (rawMaterialPercent * 100).toFixed(2)) : '0.00'}%
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Fixed Cost Amount */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.fixedCost')}</label>
            <div className="relative">
              <input
                type="text"
                value={formInputs.fixedCost}
                onChange={(e) => handleInputChange('fixedCost', e.target.value)}
                disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
                className={`w-full ps-2 pe-7 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors ${
                  !pricingData.profitMargin && !pricingData.sellingPrice
                    ? 'bg-muted border-border text-muted-foreground cursor-not-allowed' 
                    : 'border-border bg-background text-foreground'
                }`}
                placeholder="0.00"
              />
              <button
                type="button"
                onClick={() => handleReset('fixedCost')}
                disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
                className={`absolute end-1 top-1/2 -translate-y-1/2 p-0.5 ${
                  !pricingData.profitMargin && !pricingData.sellingPrice
                    ? 'text-muted-foreground cursor-not-allowed' 
                    : 'text-muted-foreground hover:text-card-foreground'
                }`}
                title={t('pricing.reset')}
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            </div>
          </div>
          
          {/* Fixed Cost Percentage Override */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.fixed')} %</label>
            <input
              type="text"
              value={formInputs.fixedCostPercentage}
              onChange={(e) => handleInputChange('fixedCostPercentage', e.target.value)}
              onBlur={() => {
                console.log(`🔍 Fixed % onBlur - Cleared currentlyEditingFieldRef`);
                currentlyEditingFieldRef.current = null;
                // Trigger real-time calculation after a small delay to ensure percentage-to-cost calculation completes
                setTimeout(() => {
                  console.log(`🔍 Fixed % onBlur - Triggering delayed real-time calculation`);
                }, 10);
              }}
              disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors ${
                !pricingData.profitMargin && !pricingData.sellingPrice
                  ? 'bg-muted border-border text-muted-foreground cursor-not-allowed' 
                  : 'border-border bg-background text-foreground'
              }`}
              placeholder={`${currentSellingPrice > 0 ? ((fixedCostPercent * 100) === 0 ? '0.00' : (fixedCostPercent * 100).toFixed(2)) : '0.00'}%`}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Variable Cost Amount */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.variableCost')}</label>
            <div className="relative">
              <input
                type="text"
                value={formInputs.variableCost}
                onChange={(e) => handleInputChange('variableCost', e.target.value)}
                disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
                className={`w-full ps-2 pe-7 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors ${
                  !pricingData.profitMargin && !pricingData.sellingPrice
                    ? 'bg-muted border-border text-muted-foreground cursor-not-allowed' 
                    : 'border-border bg-background text-foreground'
                }`}
                placeholder="0.00"
              />
              <button
                type="button"
                onClick={() => handleReset('variableCost')}
                disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
                className={`absolute end-1 top-1/2 -translate-y-1/2 p-0.5 ${
                  !pricingData.profitMargin && !pricingData.sellingPrice
                    ? 'text-muted-foreground cursor-not-allowed' 
                    : 'text-muted-foreground hover:text-card-foreground'
                }`}
                title={t('pricing.reset')}
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            </div>
          </div>
          
          {/* Variable Cost Percentage Override */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.variable')} %</label>
            <input
              type="text"
              value={formInputs.variableCostPercentage}
              onChange={(e) => handleInputChange('variableCostPercentage', e.target.value)}
              onBlur={() => {
                console.log(`🔍 Variable % onBlur - Cleared currentlyEditingFieldRef`);
                currentlyEditingFieldRef.current = null;
                // Trigger real-time calculation after a small delay to ensure percentage-to-cost calculation completes
                setTimeout(() => {
                  console.log(`🔍 Variable % onBlur - Triggering delayed real-time calculation`);
                }, 10);
              }}
              disabled={!pricingData.profitMargin && !pricingData.sellingPrice}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors ${
                !pricingData.profitMargin && !pricingData.sellingPrice
                  ? 'bg-muted border-border text-muted-foreground cursor-not-allowed' 
                  : 'border-border bg-background text-foreground'
              }`}
              placeholder={`${currentSellingPrice > 0 ? ((variableCostPercent * 100) === 0 ? '0.00' : (variableCostPercent * 100).toFixed(2)) : '0.00'}%`}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Total Cost Display (non-editable) */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.totalCost')}</label>
            <div className="px-2 py-1 text-xs border border-border bg-primary/10 rounded text-card-foreground">
              {currentTotalCost.toFixed(2)}
            </div>
          </div>
          
          {/* Total Cost Percentage Display (non-editable) */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.total')} %</label>
            <div className="px-2 py-1 text-xs border border-border bg-primary/10 rounded text-card-foreground">
              {currentSellingPrice > 0 ? ((currentTotalCost / currentSellingPrice) * 100 === 0 ? '0.00' : ((currentTotalCost / currentSellingPrice) * 100).toFixed(2)) : '0.00'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Profit Cost Field (editable) */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.profitCost')}</label>
            <input
              type="text"
              value={formInputs.profitCost}
              onChange={(e) => handleInputChange('profitCost', e.target.value)}
              onBlur={(e) => {
                console.log(`🔍 Profit Cost onBlur - Value: "${e.target.value}", isAutoCalculatingRef: ${isAutoCalculatingRef.current}`);
                // Format to 2 decimal places when user finishes editing
                const numericValue = parseFloat(e.target.value);
                if (!isNaN(numericValue)) {
                    console.log(`🔍 Profit Cost onBlur - Formatting to: ${numericValue.toFixed(2)}`);
                    setFormInputs(prev => ({
                      ...prev,
                      profitCost: numericValue.toFixed(2)
                  }));
                }
                // Reset manual editing flag
                isManuallyEditingProfitCostRef.current = false;
                console.log(`🔍 Profit Cost onBlur - Reset isManuallyEditingProfitCostRef to false`);
              }}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-background ${
                isNegativeProfitCost ? 'text-destructive' : 'text-foreground'
              } ${errors.profitCost ? 'border-destructive' : 'border-border'}`}
              placeholder="0.00"
            />
            {errors.profitCost && (
              <p className="mt-1 text-xs text-destructive">{errors.profitCost}</p>
            )}
          </div>
          
          {/* Profit Margin Percentage Field */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.margin')}</label>
            <input
              type="text"
              value={formInputs.profitMargin}
              onChange={(e) => handleInputChange('profitMargin', e.target.value)}
                onBlur={(e) => {
                  console.log(`🔍 Margin % onBlur - Value: "${e.target.value}", isAutoCalculatingRef: ${isAutoCalculatingRef.current}`);
                  // Clear the currently editing field
                  currentlyEditingFieldRef.current = null;
                  console.log(`🔍 Margin % onBlur - Cleared currentlyEditingFieldRef`);
                  // Only format if not auto-calculating
                  if (!isAutoCalculatingRef.current) {
                    const numericValue = parseFloat(e.target.value);
                    console.log(`🔍 Margin % onBlur - Formatting to: ${numericValue.toFixed(2)}`);
                    if (!isNaN(numericValue)) {
                      setFormInputs(prev => ({
                        ...prev,
                        profitMargin: numericValue.toFixed(2)
                      }));
                    }
                  } else {
                    console.log(`🔍 Margin % onBlur - Skipped formatting (auto-calculating)`);
                  }
                }}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-background ${
                isNegativeProfitMargin ? 'text-destructive' : 'text-foreground'
              } ${errors.profitMargin ? 'border-destructive' : 'border-border'}`}
              placeholder="0.00"
            />
            {errors.profitMargin && (
              <p className="mt-1 text-xs text-destructive">{errors.profitMargin}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Selling Price Amount Field */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.sellingPrice')}</label>
            <input
              type="text"
              value={formInputs.sellingPrice}
              onChange={(e) => handleInputChange('sellingPrice', e.target.value)}
                onBlur={(e) => {
                  console.log(`🔍 Selling Price onBlur - Value: "${e.target.value}", isAutoCalculatingRef: ${isAutoCalculatingRef.current}`);
                  // Clear the currently editing field
                  currentlyEditingFieldRef.current = null;
                  console.log(`🔍 Selling Price onBlur - Cleared currentlyEditingFieldRef`);
                  // Only format if not auto-calculating
                  if (!isAutoCalculatingRef.current) {
                    const numericValue = parseFloat(e.target.value);
                    console.log(`🔍 Selling Price onBlur - Formatting to: ${numericValue.toFixed(2)}`);
                    if (!isNaN(numericValue)) {
                      setFormInputs(prev => ({
                        ...prev,
                        sellingPrice: numericValue.toFixed(2)
                      }));
                    }
                  } else {
                    console.log(`🔍 Selling Price onBlur - Skipped formatting (auto-calculating)`);
                  }
                }}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                errors.sellingPrice ? 'border-destructive' : 'border-border'
              }`}
              placeholder="0.00"
            />
            {errors.sellingPrice && (
              <p className="mt-1 text-xs text-destructive">{errors.sellingPrice}</p>
            )}
          </div>
          
          {/* Selling Price Percentage Field (editable) */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.ofCost')}</label>
            <input
              type="text"
              value={formInputs.sellingPricePercentage}
              onChange={(e) => handleInputChange('sellingPricePercentage', e.target.value)}
                onBlur={(e) => {
                  console.log(`🔍 % of Cost onBlur - Value: "${e.target.value}", isAutoCalculatingRef: ${isAutoCalculatingRef.current}`);
                  // Clear the currently editing field
                  currentlyEditingFieldRef.current = null;
                  console.log(`🔍 % of Cost onBlur - Cleared currentlyEditingFieldRef`);
                  // Only format if not auto-calculating
                  if (!isAutoCalculatingRef.current) {
                    const numericValue = parseFloat(e.target.value);
                    console.log(`🔍 % of Cost onBlur - Formatting to: ${numericValue.toFixed(2)}`);
                    if (!isNaN(numericValue)) {
                      setFormInputs(prev => ({
                        ...prev,
                        sellingPricePercentage: numericValue.toFixed(2)
                      }));
                    }
                  } else {
                    console.log(`🔍 % of Cost onBlur - Skipped formatting (auto-calculating)`);
                  }
                }}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                errors.sellingPricePercentage ? 'border-destructive' : 'border-border'
              }`}
              placeholder="0.00"
            />
            {errors.sellingPricePercentage && (
              <p className="mt-1 text-xs text-destructive">{errors.sellingPricePercentage}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
        <div className="space-y-2">
          {/* Market Price Field */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.marketPrice')}</label>
            <input
              type="text"
              value={formInputs.marketPrice}
              onChange={(e) => handleInputChange('marketPrice', e.target.value)}
              onBlur={(e) => {
                const numericValue = parseFloat(e.target.value);
                if (!isNaN(numericValue)) {
                  setFormInputs(prev => ({
                    ...prev,
                    marketPrice: numericValue.toFixed(2)
                  }));
                }
              }}
              className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                errors.marketPrice ? 'border-destructive' : 'border-border'
              }`}
              placeholder="0.00"
            />
            {errors.marketPrice && (
              <p className="mt-1 text-xs text-destructive">{errors.marketPrice}</p>
            )}
          </div>
          
          {/* Market Price Percentage Difference Display */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('pricing.vsSellingPrice')}</label>
            <div className="px-2 py-1 text-xs border border-border bg-muted/50 rounded text-card-foreground">
              {(() => {
                const sellingPrice = parseFloat(formInputs.sellingPrice) || 0;
                const marketPrice = parseFloat(formInputs.marketPrice) || 0;
                if (sellingPrice > 0 && marketPrice > 0) {
                  const percentageDiff = ((marketPrice - sellingPrice) / sellingPrice) * 100;
                  let colorClass = '';
                  if (percentageDiff > 0) {
                    colorClass = 'text-chart-3';
                  } else if (percentageDiff < 0) {
                    colorClass = 'text-destructive';
                  } else {
                    colorClass = 'text-muted-foreground';
                  }
                  return (
                    <span className={`${colorClass}`}>
                      {percentageDiff > 0 ? '+' : ''}{percentageDiff.toFixed(2)}%
                    </span>
                  );
                }
                return '-';
              })()}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          pricingData.profitMargin || pricingData.sellingPrice
            ? 'bg-chart-3/10 text-chart-3' 
            : 'bg-chart-2/10 text-chart-2'
        }`} style={{ fontSize: '0.7rem' }}>
          {pricingData.profitMargin || pricingData.sellingPrice ? t('pricing.pricingSet') : t('pricing.noPricing')}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs font-medium" style={{ fontSize: '0.7rem' }}>
        <div className="inline-flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaveDisabled || isLoading}
            className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
            title={t('pricing.save')}
          >
            <Save className="h-3 w-3" />
          </button>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
            title={t('pricing.cancel')}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const PricingListPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  
  
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pricingData, setPricingData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('id');
  const categoryDropdownRef = useRef(null);
  
  // Tab state
  const [costingDefaults, setCostingDefaults] = useState({});
  const [isSaving, setIsSaving] = useState(false);
 // 'dishes' or 'applications'
  
  // Inline editing state
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  
  // Load recipes and pricing data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load all recipes and filter to only show those with has_pricing = true
        const [allRecipesData, categoriesData] = await Promise.all([
          recipesAPI.getAll(),
          recipesAPI.getCategories().catch(() => [])
        ]);
        const recipesData = allRecipesData.filter(recipe => recipe.has_pricing !== false);
        setRecipes(recipesData);
        setCategories(categoriesData || []);
        
        // Load pricing data for all recipes
        try {
          const pricingData = await pricingAPI.getAllPricing();
          const pricingMap = {};
          pricingData.forEach(pricing => {
            pricingMap[pricing.recipe_id] = pricing;
          });
          setPricingData(pricingMap);
        } catch (pricingError) {
          // No pricing data available yet
          console.log('No pricing data found');
        }

        // Load overhead distribution data for cost per dish calculation
        const defaultsMap = {};
        
        try {
          // Get fixed and variable overhead distribution data using the API functions
          const { fixedOverheadAPI, variableOverheadAPI } = await import('../../utils/api');
          
          const [fixedOverheadData, variableOverheadData] = await Promise.all([
            fixedOverheadAPI.getDistributionSummary().catch(() => ({ distribution: [] })),
            variableOverheadAPI.getDistributionSummary().catch(() => ({ distribution: [] }))
          ]);
          
          
          // Create maps for quick lookup by category_id
          const fixedDistributionMap = {};
          const variableDistributionMap = {};
          
          fixedOverheadData.distribution?.forEach(item => {
            fixedDistributionMap[item.category_id] = item;
          });
          
          variableOverheadData.distribution?.forEach(item => {
            variableDistributionMap[item.category_id] = item;
          });
          
          
          // Calculate cost per dish for each recipe
          for (const recipe of recipesData) {
            const categoryId = recipe.category_id || 0;
            const fixedDistribution = fixedDistributionMap[categoryId];
            const variableDistribution = variableDistributionMap[categoryId];
            
            
            // Calculate cost per dish using the same logic as overhead tables
            const fixedCostPerDish = fixedDistribution?.cost_per_dish || 0;
            const variableCostPerDish = variableDistribution?.cost_per_dish || 0;
            
            defaultsMap[recipe.id] = {
              fixedCost: fixedCostPerDish,
              variableCost: variableCostPerDish,
              recipe: {
                id: recipe.id,
                name_en: recipe.name_en,
                name_ar: recipe.name_ar,
                category_id: recipe.category_id,
                servings: recipe.servings
              }
            };
          }
          
          setCostingDefaults(defaultsMap);
        } catch (error) {
          console.error('Error loading overhead distribution data:', error);
          // Fallback to empty defaults
          for (const recipe of recipesData) {
            defaultsMap[recipe.id] = {
              fixedCost: 0,
              variableCost: 0,
              recipe: {
                id: recipe.id,
                name_en: recipe.name_en,
                name_ar: recipe.name_ar,
                category_id: recipe.category_id,
                servings: recipe.servings
              }
            };
          }
          setCostingDefaults(defaultsMap);
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Handle clicks outside category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };

    if (isCategoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryOpen]);

  const getCategoryLabel = (category) =>
    i18n.language === 'ar'
      ? (category.name_ar || category.name_en)
      : (category.name_en || category.name_ar);

  const selectedCategoryLabel = (() => {
    if (categoryFilter.length === 0) return null;
    if (categoryFilter.length === 1) {
      const selected = categories.find((cat) => cat.id === categoryFilter[0]);
      return selected ? getCategoryLabel(selected) : null;
    }
    return t('pricing.categoriesSelected', { count: categoryFilter.length });
  })();

  const toggleCategoryFilter = (categoryId) => {
    setCategoryFilter((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  
  // Filter and sort recipes based on category and search term
  const filteredRecipes = recipes
    .filter((recipe) => {
      const matchesCategory =
        categoryFilter.length === 0 ||
        categoryFilter.includes(recipe.category_id ?? 0);
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        recipe.name_en?.toLowerCase().includes(q) ||
        recipe.name_ar?.toLowerCase().includes(q) ||
        recipe.id?.toString().includes(searchTerm);
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      if (sortOrder === 'id') return (a.id || 0) - (b.id || 0);
      return (a.name_en || '').localeCompare(b.name_en || '');
    });
  
  // Handle inline editing start
  const handleStartEdit = (recipeId) => {
    // Check if defaults are available for this recipe
    if (!costingDefaults[recipeId]) {
      console.error('Cannot start editing: defaults not available for recipe', recipeId);
      alert('Costing defaults not available for this recipe. Please refresh the page and try again.');
      return;
    }
    setEditingRecipeId(recipeId);
  };
  
  // Handle inline editing cancel
  const handleCancelEdit = () => {
    setEditingRecipeId(null);
  };
  
  // Handle save pricing from inline editing
  const handleSavePricing = async (recipeId, pricingPayload) => {
    try {
      setIsSaving(true);
      
      // Check if pricing data already exists for this recipe
      const hasExistingPricing = pricingData[recipeId];
      
      let savedPricing;
      if (hasExistingPricing) {
        // Update existing pricing
        savedPricing = await pricingAPI.updatePricing(recipeId, pricingPayload);
      } else {
        // Create new pricing
        savedPricing = await pricingAPI.savePricing(recipeId, pricingPayload);
      }
      
      // Update local state with the complete saved data from the server
      setPricingData(prev => ({
        ...prev,
        [recipeId]: savedPricing
      }));
      
      // Exit editing mode
      setEditingRecipeId(null);
      
    } catch (err) {
      console.error('Error saving pricing:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle create new pricing
  const handleCreatePricing = () => {
    // Navigate to recipe selection or create new recipe
    navigate('/dish-costing/recipes');
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('pricing.loadingRecipes')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 bg-background text-foreground min-h-screen p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('pricing.title')}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {t('pricing.subtitle')}
            </p>
          </div>
        </div>
      </div>
      
      
      {/* Content */}
      <div className="space-y-6">
          {/* Search, Category Filter, and Sort */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder={t('pricing.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
                  style={{ 
                    paddingLeft: '2.75rem',
                    paddingRight: i18n.language === 'ar' ? '1.25rem' : '1rem'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter (multi-select) */}
            <div className="relative w-full sm:w-56 flex-shrink-0" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                aria-label={t('pricing.filterByCategory')}
                aria-expanded={isCategoryOpen}
                className={`w-full h-10 flex items-center px-3 border border-border rounded-lg shadow-sm ${i18n.language === 'ar' ? 'text-right flex-row-reverse' : 'text-left justify-between'} bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors`}
              >
                <span
                  className={`${categoryFilter.length > 0 ? 'text-card-foreground' : 'text-muted-foreground'} truncate`}
                  title={selectedCategoryLabel || t('common.selectCategory')}
                >
                  {selectedCategoryLabel || t('common.selectCategory')}
                </span>
                <svg className="h-4 w-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isCategoryOpen && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => setCategoryFilter([])}
                      className={`w-full px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${
                        categoryFilter.length === 0 ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                      } ${i18n.language === 'ar' ? 'flex-row-reverse text-right' : 'text-left'}`}
                    >
                      <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                        {categoryFilter.length === 0 && <Check className="h-4 w-4" />}
                      </span>
                      <span className="truncate">{t('pricing.allCategories')}</span>
                    </button>
                    {categories.map((category) => {
                      const label = getCategoryLabel(category);
                      const isSelected = categoryFilter.includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategoryFilter(category.id)}
                          className={`w-full px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${
                            isSelected ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                          } ${i18n.language === 'ar' ? 'flex-row-reverse text-right' : 'text-left'}`}
                          title={label}
                        >
                          <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                            {isSelected && <Check className="h-4 w-4" />}
                          </span>
                          <span className="truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Sort Button */}
            <button
              onClick={(e) => {
                setSortOrder(sortOrder === 'id' ? 'alphabetical' : 'id');
                e.target.blur();
              }}
              className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors flex-shrink-0"
              title={sortOrder === 'id' ? t('pricing.sortById') : t('pricing.sortByName')}
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>
          </div>
      
          {/* Dishes Table */}
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-card-foreground">{t('pricing.dishes')}</h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-chart-2/10 text-chart-2" style={{ fontSize: '0.6rem' }}>M</span>
                  <span>{t('pricing.modifiedFromDefault')}</span>
                </div>
              </div>
            </div>
            <div className="p-0">
            
      {filteredRecipes.length === 0 ? (
        <div className="text-center py-12">
          <ChefHat className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-card-foreground mb-2">
            {searchTerm ? t('pricing.noRecipesFound') : t('pricing.noRecipesAvailable')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm 
              ? t('pricing.tryAdjustingSearchTerms')
              : t('pricing.createFirstRecipe')
            }
          </p>
          {!searchTerm && (
            <button
              onClick={handleCreatePricing}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('pricing.createRecipe')}
            </button>
          )}
        </div>
      ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.id')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.dish')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.dishCostPerServing')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.fixedCost')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.variableCost')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.totalCost')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.profitMargin')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.sellingPrice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.marketPrice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.pricingStatus')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
          {filteredRecipes.map((recipe) => {
            const pricing = pricingData[recipe.id];
            const defaults = costingDefaults[recipe.id];
            const hasPricing = !!pricing;
            const isEditing = editingRecipeId === recipe.id;
            
            // If this recipe is being edited, show the editing row
            if (isEditing) {
              return (
                <PricingRow
                  key={recipe.id}
                  recipe={recipe}
                  pricing={pricing}
                  defaults={defaults}
                  onSave={handleSavePricing}
                  onCancel={handleCancelEdit}
                  isLoading={isSaving}
                />
              );
            }
            
            // Calculate total cost for percentage calculations
            // Raw Material Cost now depends on Total Cost Per Serving
            const rawMaterialCost = parseFloat(recipe.cost_per_serving) || 0;
            
            // Get default values from overhead distribution
            const defaultFixedCost = parseFloat(defaults?.fixedCost) || 0;
            const defaultVariableCost = parseFloat(defaults?.variableCost) || 0;
            
            // If pricing is set, use the database values (fixed_cost_override and variable_cost_override)
            // If no pricing is set, use the defaults from overhead distribution
            const fixedCost = hasPricing 
              ? (parseFloat(pricing.fixed_cost_override) || 0)  // Use database value, default to 0 if null
              : defaultFixedCost;        // Use overhead distribution default
            const variableCost = hasPricing 
              ? (parseFloat(pricing.variable_cost_override) || 0)  // Use database value, default to 0 if null
              : defaultVariableCost;         // Use overhead distribution default
            
            // Check if costs have been modified from defaults (only for dishes with pricing)
            const isFixedCostModified = hasPricing && pricing.fixed_cost_override !== null && 
              Math.abs(fixedCost - defaultFixedCost) > 0.01; // Allow for small floating point differences
            const isVariableCostModified = hasPricing && pricing.variable_cost_override !== null && 
              Math.abs(variableCost - defaultVariableCost) > 0.01; // Allow for small floating point differences
            
            // Total cost is now: Raw Material Cost (per serving) + Fixed Cost + Variable Cost
            const totalCost = rawMaterialCost + fixedCost + variableCost;
            
            
            // Calculate cost breakdown percentages as percentage of selling price
            const sellingPrice = parseFloat(pricing?.selling_price) || 0;
            const rawMaterialPercentage = sellingPrice > 0 ? Math.round(((rawMaterialCost / sellingPrice) * 100) * 100) / 100 : 0;
            const fixedCostPercentage = sellingPrice > 0 ? Math.round(((fixedCost / sellingPrice) * 100) * 100) / 100 : 0;
            const variableCostPercentage = sellingPrice > 0 ? Math.round(((variableCost / sellingPrice) * 100) * 100) / 100 : 0;
            
            // Calculate profit cost using database values for consistency
            const profitCost = pricing?.profit_margin !== null && pricing?.profit_margin !== undefined && pricing?.total_cost 
              ? Math.round(((pricing.profit_margin / 100) * pricing.total_cost) / (1 - (pricing.profit_margin / 100)) * 1000) / 1000 
              : 0;
            const marginValue = parseFloat(pricing?.profit_margin ?? 0);
            const isNegativeMargin = marginValue < 0;
            const profitDisplayColorClass = isNegativeMargin
              ? 'text-destructive'
              : marginValue > 0
                ? 'text-chart-3'
                : 'text-muted-foreground';
            
            return (
                        <tr key={recipe.id} className="hover:bg-muted/50">
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground" style={{ fontSize: '0.7rem' }}>
                            #{recipe.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <div className="dish-item flex items-start justify-start">
                              <div className="flex-shrink-0 h-10 w-10">
                  {recipe.image_url ? (
                    <img
                                    className="h-10 w-10 rounded-lg object-cover"
                      src={recipe.image_url}
                      alt={recipe.name_en}
                    />
                  ) : (
                                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                                    <ChefHat className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <div className="ml-4 text-left">
                                {i18n.language === 'ar' ? (
                                  <>
                                    {recipe.name_ar && (
                                      <div className="text-xs font-medium text-card-foreground font-arabic" style={{ fontSize: '0.7rem' }}>
                                        {recipe.name_ar}
                                      </div>
                                    )}
                                    <div className="text-xs text-muted-foreground" style={{ fontSize: '0.7rem' }}>
                                      {recipe.name_en}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-xs font-medium text-card-foreground" style={{ fontSize: '0.7rem' }}>
                                      {recipe.name_en}
                                    </div>
                                    {recipe.name_ar && (
                                      <div className="text-xs text-muted-foreground font-arabic" style={{ fontSize: '0.7rem' }}>
                                        {recipe.name_ar}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            <div className="font-medium">
                              {formatCurrency(rawMaterialCost)}
                            </div>
                            <div className="text-muted-foreground">
                              {rawMaterialPercentage === 0 ? '0%' : rawMaterialPercentage.toFixed(2) + '%'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">
                                  {formatCurrency(fixedCost)}
                                </span>
                                {isFixedCostModified && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-chart-2/10 text-chart-2" title={`Modified from default: ${formatCurrency(defaultFixedCost)}`} style={{ fontSize: '0.5rem' }}>
                                    M
                                  </span>
                                )}
                              </div>
                              <div className="text-muted-foreground">
                                {fixedCostPercentage === 0 ? '0%' : fixedCostPercentage.toFixed(2) + '%'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">
                                  {formatCurrency(variableCost)}
                                </span>
                                {isVariableCostModified && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-chart-2/10 text-chart-2" title={`Modified from default: ${formatCurrency(defaultVariableCost)}`} style={{ fontSize: '0.5rem' }}>
                                    M
                                  </span>
                                )}
                              </div>
                              <div className="text-muted-foreground">
                                {variableCostPercentage === 0 ? '0%' : variableCostPercentage.toFixed(2) + '%'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            <div className="font-medium">
                              {formatCurrency(totalCost)}
                            </div>
                            <div className="text-muted-foreground">
                              {sellingPrice > 0 ? ((totalCost / sellingPrice) * 100 === 0 ? '0%' : ((totalCost / sellingPrice) * 100).toFixed(2) + '%') : '0%'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            {hasPricing && pricing.profit_margin !== null && pricing.profit_margin !== undefined ? (
                              <>
                                <div className={`font-medium ${profitDisplayColorClass}`}>
                                  {formatCurrency(profitCost)}
                                </div>
                                <div className={profitDisplayColorClass}>
                                  {marginValue === 0 ? '0%' : marginValue.toFixed(2) + '%'}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            {hasPricing ? (
                              <>
                                <div className="font-medium">
                                  {formatCurrency(parseFloat(pricing.selling_price) || 0)}
                                </div>
                                <div className="text-muted-foreground">
                                  {(() => {
                                    const sellingPricePercentage = totalCost > 0 ? (Math.round((((parseFloat(pricing.selling_price) || 0) / totalCost) * 100) * 1000) / 1000) : 0;
                                    return sellingPricePercentage === 0 ? '0%' : sellingPricePercentage.toFixed(2) + '%';
                                  })()}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-card-foreground" style={{ fontSize: '0.7rem' }}>
                            {hasPricing && pricing.market_price ? (
                              <>
                                <div className="font-medium">
                                  {formatCurrency(parseFloat(pricing.market_price) || 0)}
                                </div>
                                {(() => {
                                  const sellingPrice = parseFloat(pricing.selling_price) || 0;
                                  const marketPrice = parseFloat(pricing.market_price) || 0;
                                  if (sellingPrice > 0) {
                                    const percentageDiff = ((marketPrice - sellingPrice) / sellingPrice) * 100;
                                    let colorClass = '';
                                    if (percentageDiff > 0) {
                                      colorClass = 'text-chart-3';
                                    } else if (percentageDiff < 0) {
                                      colorClass = 'text-destructive';
                                    } else {
                                      colorClass = 'text-muted-foreground';
                                    }
                                    return (
                                      <div className={`text-xs ${colorClass}`}>
                                        {percentageDiff > 0 ? '+' : ''}{percentageDiff.toFixed(2)}%
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              hasPricing 
                                ? 'bg-chart-3/10 text-chart-3' 
                                : 'bg-chart-2/10 text-chart-2'
                            }`} style={{ fontSize: '0.7rem' }}>
                              {hasPricing ? t('pricing.pricingSet') : t('pricing.noPricing')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs font-medium" style={{ fontSize: '0.7rem' }}>
                            <button
                              onClick={() => handleStartEdit(recipe.id)}
                              className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {hasPricing ? t('pricing.editPricing') : t('pricing.setPricing')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default PricingListPage;
